/**
 * Network transport.
 *
 * Only two things in this app are allowed to touch the network: this module and
 * the service worker. Screens talk to Dexie. That separation is what makes
 * offline behaviour a property of the architecture rather than a per-screen
 * feature someone has to remember.
 */
import {
  ApiClient,
  ApiClientError,
  type AuthTokens,
  type ChangePasswordInput,
  type CheckpointOption,
  type UpdateProfileInput,
  type CreateUserInput,
  type LoginInput,
  type ScanEventInput,
  type ScanResponse,
  type SyncPullResponse,
  type SyncPushRequest,
  type SyncPushResponse,
  type ScanHistoryRow,
  type Paginated,
  type AuditLogRow,
  type CreateJobInput,
  type CreateScheduleInput,
  type CreateShotReadingInput,
  type CreateJourneyInput,
  type CreateRouteInput,
  type CreateTransferInput,
  type JourneyRow,
  type RouteRow,
  type FailureReasonRow,
  type MaintenanceJobRow,
  type MaintenanceScheduleRow,
  type ReportSummary,
  type ShotReadingRow,
  type StockTakeDetail,
  type StockTakeRow,
  type UpdateJobInput,
  type TransferDetail,
  type TransferRow,
  type UpdateTransferInput,
  type UpdateUserInput,
  type User,
  type MouldPhoto,
  type ImportPreviewRequest,
  type ImportPreviewResponse,
  type ImportCommitRequest,
  type ImportCommitResponse,
  type ImportCompleteRequest,
  type ImportDeleteRequest,
  type ImportDeleteResponse,
  type ImportHistoryQuery,
  type ImportRecordsResponse,
  type ImportRunRow,
  type MouldImportRef,
  type AnalyticsQuery,
  type AnalyticsOverview,
  type BackupStatusDto,
  type BackupSettingsDto,
  type BackupRunDto,
  type UpdateBackupSettingsInput,
} from '@mouldtrack/shared';
import { clearSession, getAccessToken, getRefreshToken, setAccessToken } from '../auth/session.js';

/** Dev proxies /api to the API server; production serves both from one origin. */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

let onAuthLost: (() => void) | null = null;
export function setAuthFailureHandler(fn: () => void): void {
  onAuthLost = fn;
}

export const api = new ApiClient({
  baseUrl: BASE_URL,
  getAccessToken,
  getRefreshToken,
  onTokensRefreshed: setAccessToken,
  onAuthFailure: () => {
    // The refresh token itself is dead - no amount of retrying recovers this.
    // Offline we never get here, because the request fails before it 401s.
    void clearSession().then(() => onAuthLost?.());
  },
});

export { ApiClientError };

/**
 * True when the request failed because there was no usable network, as opposed
 * to the server answering with an error. The distinction drives everything: a
 * network failure means "retry later, keep the user working", while a 4xx means
 * "this mutation is never going to succeed, surface it".
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof ApiClientError) return false;
  if (!navigator.onLine) return true;
  // A request that hit its deadline is a transport problem, not a server answer:
  // the connection accepted the socket and then said nothing. Treating it as a
  // hard error would show the user a failure for something worth simply
  // retrying. AbortSignal.timeout rejects with a DOMException, not a TypeError.
  if (error instanceof DOMException && (error.name === 'TimeoutError' || error.name === 'AbortError')) {
    return true;
  }
  return error instanceof TypeError; // fetch rejects with TypeError on transport failure
}

// --- auth ---

export function login(input: LoginInput): Promise<AuthTokens> {
  return api.login(input);
}

export function fetchMe(): Promise<User> {
  return api.me();
}

export function changePassword(input: ChangePasswordInput): Promise<{ ok: true }> {
  return api.changePassword(input);
}

/** Edit your own profile. Online only - identity is not a local-first concern. */
export function updateProfile(input: UpdateProfileInput): Promise<User> {
  return api.updateProfile(input);
}

// --- scans ---

/** A mould's movement record. Online-only: the ledger is not replicated. */
export function listMouldScans(
  mouldId: string,
  page = 1,
): Promise<Paginated<ScanHistoryRow>> {
  return api.listMouldScans(mouldId, 50, page);
}

/**
 * Record one scan.
 *
 * Deliberately not routed through the sync outbox. That queue carries catalog
 * edits, which conflict and need `baseUpdatedAt`; a scan is an append-only fact
 * about a moment that has already happened and can never conflict with anything.
 * Its safety comes from `clientScanId` instead - send it twice and the server
 * returns the first row rather than inventing a second movement.
 */
export function postScan(input: ScanEventInput): Promise<ScanResponse> {
  return api.postScan(input);
}

/** Flush a queue of scans taken offline. Per-item results; never fails wholesale. */
export function postScanBatch(scans: ScanEventInput[]) {
  return api.postScanBatch(scans);
}

/**
 * Every checkpoint this account may see.
 *
 * Fetched rather than read from Dexie on purpose: the scanner needs the site and
 * store *names* hanging off each checkpoint, and the replicated `checkpoints`
 * table holds only foreign keys. The result is cached by the caller so the
 * screen still works with no connection.
 */
export function listCheckpoints(): Promise<CheckpointOption[]> {
  return api.listCheckpoints().then((r) => r.data);
}

// --- users (admin) ---
//
// Accounts are not part of the replicated catalog - they are not a sync table,
// and a device holding a roster of colleagues would be a liability rather than
// a feature. So unlike every other screen, the people screens read straight
// from the server and need a connection.

export function listUsers(): Promise<User[]> {
  return api.listUsers().then((r) => r.data);
}

export function createUser(input: CreateUserInput): Promise<User> {
  return api.createUser(input);
}

export function updateUser(id: string, input: UpdateUserInput): Promise<User> {
  return api.updateUser(id, input);
}

export function deleteUser(id: string): Promise<void> {
  return api.deleteUser(id);
}

// --- transfers ---
//
// Online-only, like the People screen: a transfer is paperwork about a movement
// that is happening now, and a stale local copy of it is worse than none.

export function listTransfers(query = ''): Promise<TransferRow[]> {
  return api.listTransfers(query).then((r) => r.data);
}

export function getTransfer(idOrNo: string): Promise<TransferDetail> {
  return api.getTransfer(idOrNo);
}

export function createTransfer(input: CreateTransferInput): Promise<TransferDetail> {
  return api.createTransfer(input);
}

export function updateTransfer(
  idOrNo: string,
  input: UpdateTransferInput,
): Promise<TransferDetail> {
  return api.updateTransfer(idOrNo, input);
}

// --- journeys ---
//
// Online-only, for the same reason transfers are: a journey is a live claim
// about where a tool is right now, and a stale local copy of that is worse
// than none.

export function listJourneys(query = ''): Promise<JourneyRow[]> {
  return api.listJourneys(query).then((r) => r.data);
}

export function getJourney(id: string): Promise<JourneyRow> {
  return api.getJourney(id).then((r) => r.data);
}

export function createJourney(input: CreateJourneyInput): Promise<JourneyRow> {
  return api.createJourney(input).then((r) => r.data);
}

export function updateJourney(id: string, action: 'start' | 'cancel'): Promise<JourneyRow> {
  return api.updateJourney(id, action).then((r) => r.data);
}

export function listRoutes(): Promise<RouteRow[]> {
  return api.listRoutes().then((r) => r.data);
}

export function createRoute(input: CreateRouteInput): Promise<RouteRow> {
  return api.createRoute(input).then((r) => r.data);
}

// --- maintenance (v4) ---
//
// Online-only. A job is a live claim about work happening now, and the reading
// ledger is append-only and unbounded; neither belongs in a device's copy.

export function listJobs(query = ''): Promise<MaintenanceJobRow[]> {
  return api.listJobs(query).then((r) => r.data);
}
export function createJob(input: CreateJobInput): Promise<MaintenanceJobRow> {
  return api.createJob(input);
}
export function updateJob(idOrNo: string, input: UpdateJobInput): Promise<MaintenanceJobRow> {
  return api.updateJob(idOrNo, input);
}
export function listSchedules(query = ''): Promise<MaintenanceScheduleRow[]> {
  return api.listSchedules(query).then((r) => r.data);
}
export function createSchedule(input: CreateScheduleInput): Promise<MaintenanceScheduleRow> {
  return api.createSchedule(input);
}
export function deleteSchedule(id: string): Promise<void> {
  return api.deleteSchedule(id);
}
export function listShotReadings(query = ''): Promise<ShotReadingRow[]> {
  return api.listShotReadings(query).then((r) => r.data);
}
export function createShotReading(input: CreateShotReadingInput): Promise<ShotReadingRow> {
  return api.createShotReading(input);
}
export function listFailureReasons(): Promise<FailureReasonRow[]> {
  return api.listFailureReasons().then((r) => r.data);
}

// --- audit, stock take and reports (v5) ---

export function listAuditLog(query = ''): Promise<AuditLogRow[]> {
  return api.listAuditLog(query).then((r) => r.data);
}
export function listStockTakes(query = ''): Promise<StockTakeRow[]> {
  return api.listStockTakes(query).then((r) => r.data);
}
export function getStockTake(idOrRef: string): Promise<StockTakeDetail> {
  return api.getStockTake(idOrRef);
}
export function startStockTake(locationId: string): Promise<StockTakeRow> {
  return api.startStockTake(locationId);
}
export function scanStockTake(idOrRef: string, assetCode: string) {
  return api.scanStockTake(idOrRef, assetCode);
}
export function closeStockTake(idOrRef: string, notes?: string | null) {
  return api.closeStockTake(idOrRef, notes);
}
export function reportSummary(days = 90): Promise<ReportSummary> {
  return api.reportSummary(days);
}

// --- sync ---

export function syncPull(since?: string): Promise<SyncPullResponse> {
  const query = since ? `?since=${encodeURIComponent(since)}` : '';
  return api.get<SyncPullResponse>(`/sync/pull${query}`);
}

export function syncPush(body: SyncPushRequest): Promise<SyncPushResponse> {
  return api.post<SyncPushResponse>('/sync/push', body);
}

// --- binary ---

export function downloadBlob(path: string): Promise<Blob> {
  return api.getBlob(path);
}

/**
 * The plate designs on offer.
 *
 * Online only, like the nameplate itself: the plate is drawn on the server, so
 * a device with no signal has nothing to compare.
 */
export function listNameplateDesigns() {
  return api.listNameplateDesigns();
}

export interface EngraveBatchResult {
  jobId: string;
  fileName: string;
  design: string;
  total: number;
  generated: number;
  failed: number;
  failures: Array<{ mouldId: string; code: string; reason: string }>;
  /** Plates with a detail engraved blank - drawn all the same, not failures. */
  withBlankDetails: number;
  bytes: number;
  createdAt: string;
}

/**
 * Draw the engrave file of every mould asked for (every mould in the catalog
 * when `mouldIds` is left out) into one ZIP on the server. Slow by nature - a
 * thousand plates take a while - hence the long deadline.
 */
export function generateEngraveFiles(body: { mouldIds?: string[]; design?: string | null }): Promise<EngraveBatchResult> {
  return api.postSlow<EngraveBatchResult>('/nameplates/batch', body, 15 * 60_000);
}

/** The ZIP a batch produced; kept on the server for an hour. */
export function downloadEngraveFiles(jobId: string): Promise<Blob> {
  return api.getBlob(`/nameplates/batch/${encodeURIComponent(jobId)}`, true, 15 * 60_000);
}

/** One design drawn for one mould, as SVG, without changing the record. */
export async function nameplatePreview(assetCode: string, design: string): Promise<string> {
  const blob = await api.getBlob(
    `/moulds/${encodeURIComponent(assetCode)}/nameplate?format=svg&design=${encodeURIComponent(design)}`,
  );
  return blob.text();
}

// --- photos (online only: the gallery is not replicated) ---

export function listMouldPhotos(mould: string): Promise<MouldPhoto[]> {
  return api.get<MouldPhoto[]>(`/moulds/${encodeURIComponent(mould)}/photos`);
}

/** Every photo of every mould, in gallery order - for the catalog export. */
export function listAllPhotos(): Promise<MouldPhoto[]> {
  return api.get<MouldPhoto[]>('/photos');
}

/**
 * Send one picture. Raw bytes, one request each - a photo inside a JSON row is
 * what made batches too large to arrive.
 */
export function uploadMouldPhoto(
  mould: string,
  file: Blob,
  opts: { primary?: boolean; importRunId?: string } = {},
): Promise<{ photo: MouldPhoto; duplicate: boolean }> {
  const query = new URLSearchParams();
  if (opts.primary) query.set('primary', '1');
  if (opts.importRunId) {
    query.set('source', 'import');
    query.set('importRunId', opts.importRunId);
  }
  const qs = query.toString();
  return api.upload(
    `/moulds/${encodeURIComponent(mould)}/photos${qs ? `?${qs}` : ''}`,
    file,
    file.type || 'application/octet-stream',
  );
}

export function makePrimaryPhoto(mould: string, photoId: string): Promise<MouldPhoto[]> {
  return api.post<MouldPhoto[]>(`/moulds/${encodeURIComponent(mould)}/photos/${encodeURIComponent(photoId)}/primary`);
}

export function removeMouldPhoto(mould: string, photoId: string): Promise<MouldPhoto[]> {
  return api.del<MouldPhoto[]>(`/moulds/${encodeURIComponent(mould)}/photos/${encodeURIComponent(photoId)}`);
}

// --- imports (online only: "imported" is the server's word) ---

export function previewImport(body: ImportPreviewRequest): Promise<ImportPreviewResponse> {
  return api.postSlow<ImportPreviewResponse>('/imports/preview', body, 120_000);
}

export function commitImport(body: ImportCommitRequest): Promise<ImportCommitResponse> {
  return api.postSlow<ImportCommitResponse>('/imports', body, 240_000);
}

export function completeImport(runId: string, body: ImportCompleteRequest): Promise<ImportRunRow> {
  return api.post<ImportRunRow>(`/imports/${encodeURIComponent(runId)}/complete`, body);
}

export function listImportRuns(limit = 25): Promise<ImportRunRow[]> {
  return api.get<ImportRunRow[]>(`/imports?limit=${limit}`);
}

/** Import History: the imported files, searched, filtered, sorted, a page at a time. */
export function listImportHistory(query: ImportHistoryQuery): Promise<Paginated<ImportRunRow>> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  return api.get<Paginated<ImportRunRow>>(`/imports/history?${params.toString()}`);
}

/** One import's rows, and what each became. */
export function getImportRecords(runId: string): Promise<ImportRecordsResponse> {
  return api.get<ImportRecordsResponse>(`/imports/${encodeURIComponent(runId)}/records`);
}

/** Delete the moulds an import added, its place in Import History, or both. */
export function deleteImportData(runId: string, body: ImportDeleteRequest): Promise<ImportDeleteResponse> {
  return api.post<ImportDeleteResponse>(`/imports/${encodeURIComponent(runId)}/delete`, body);
}

/** The imports that brought in or changed one mould. */
export function listMouldImports(mouldId: string): Promise<MouldImportRef[]> {
  return api.get<MouldImportRef[]>(`/imports/by-mould/${encodeURIComponent(mouldId)}`);
}

// --- analysis ---

export function analyticsOverview(query: AnalyticsQuery): Promise<AnalyticsOverview> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) params.set(k, String(v));
  const qs = params.toString();
  return api.get<AnalyticsOverview>(`/analytics/overview${qs ? `?${qs}` : ''}`);
}

// --- backups (administrators) ---

export function backupStatus(): Promise<BackupStatusDto> {
  return api.get<BackupStatusDto>('/backups');
}

export function updateBackupSettings(input: UpdateBackupSettingsInput): Promise<BackupSettingsDto> {
  return api.put<BackupSettingsDto>('/backups/settings', input);
}

export function startBackup(): Promise<BackupRunDto> {
  return api.post<BackupRunDto>('/backups');
}

export function verifyBackup(runId: string): Promise<BackupRunDto> {
  return api.post<BackupRunDto>(`/backups/${encodeURIComponent(runId)}/verify`);
}
